// Ward Incharge Management Tab (three-tier nursing hierarchy).
// Exclusively available to Nursing Superintendents and Facility Administrators
// on the Staff Roster view. Enables the Superintendent to assign, transfer,
// and oversee Ward Incharges across all hospital wards/room categories.
import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import { toast } from "react-toastify";

import { NURSE_COLORS } from "../theme";
import {
  getInchargeAssignments,
  assignWardIncharge,
  removeWardIncharge,
  listOf,
} from "../api/nurseApi";

export default function InchargeManagementTab({
  wards = [],
  nurses = [],
  onChanged,
}) {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedWard, setSelectedWard] = useState("");
  const [selectedNurseId, setSelectedNurseId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    getInchargeAssignments()
      .then((res) => {
        setAssignments(listOf(res));
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("Failed to load incharge assignments:", err);
        setAssignments([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleOpenDialog = (wardCode = "") => {
    setSelectedWard(wardCode);
    setSelectedNurseId("");
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedWard("");
    setSelectedNurseId("");
  };

  const handleAssign = async () => {
    if (!selectedWard || !selectedNurseId) {
      toast.error("Please select both a ward and a nurse.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await assignWardIncharge({
        ward_code: selectedWard,
        nurse_id: selectedNurseId,
      });
      if (res?.status === "Success") {
        toast.success(res.message || "Ward Incharge assigned successfully.");
        handleCloseDialog();
        reload();
        onChanged?.();
      } else {
        toast.error(res?.message || "Failed to assign Ward Incharge.");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to assign Ward Incharge."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (assignmentId) => {
    try {
      const res = await removeWardIncharge(assignmentId);
      if (res?.status === "Success") {
        toast.success("Ward Incharge assignment removed.");
        reload();
        onChanged?.();
      } else {
        toast.error(res?.message || "Failed to remove assignment.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to remove assignment.");
    }
  };

  // Group active assignments by ward_code
  const assignmentsByWard = React.useMemo(() => {
    const map = {};
    assignments.forEach((a) => {
      if (!map[a.ward_code]) map[a.ward_code] = [];
      map[a.ward_code].push(a);
    });
    return map;
  }, [assignments]);

  return (
    <Box sx={{ mt: 1 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Ward Incharge Allocation
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Assign and supervise Ward Incharges responsible for ward-level
            rostering and operational management.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            bgcolor: NURSE_COLORS.primary,
            "&:hover": { bgcolor: "#092542" },
            textTransform: "none",
          }}
        >
          Assign Ward Incharge
        </Button>
      </Stack>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: "#f8fafc" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Ward / Room Category</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Assigned Ward Incharge(s)</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Assigned By</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Assigned At</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {wards.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  No wards available.
                </TableCell>
              </TableRow>
            ) : (
              wards.map((ward) => {
                const inchargeList = assignmentsByWard[ward.code] || [];
                return (
                  <TableRow key={ward.code} hover>
                    <TableCell sx={{ fontWeight: 500 }}>
                      <Stack direction="row" alignItems="center" gap={1}>
                        <SupervisorAccountIcon
                          fontSize="small"
                          sx={{ color: NURSE_COLORS.primary }}
                        />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {ward.name || ward.code}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            Code: {ward.code}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {inchargeList.length === 0 ? (
                        <Chip
                          label="No Incharge Assigned"
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      ) : (
                        <Stack direction="row" gap={1} flexWrap="wrap">
                          {inchargeList.map((inc) => (
                            <Chip
                              key={inc.id}
                              label={inc.nurse_name || inc.nurse_id}
                              size="small"
                              sx={{
                                bgcolor: "rgba(33,205,192,0.15)",
                                color: "#00695c",
                                fontWeight: 600,
                              }}
                              onDelete={() => handleRemove(inc.id)}
                            />
                          ))}
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {inchargeList[0]?.assigned_by || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {inchargeList[0]?.assigned_at
                          ? new Date(inchargeList[0].assigned_at).toLocaleDateString()
                          : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleOpenDialog(ward.code)}
                        sx={{ textTransform: "none", fontSize: 12, mr: 1 }}
                      >
                        {inchargeList.length > 0 ? "Add / Reassign" : "Assign"}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Assignment Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Assign Ward Incharge</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="select-ward-label">Ward</InputLabel>
              <Select
                labelId="select-ward-label"
                value={selectedWard}
                label="Ward"
                onChange={(e) => setSelectedWard(e.target.value)}
              >
                {wards.map((w) => (
                  <MenuItem key={w.code} value={w.code}>
                    {w.name || w.code} ({w.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel id="select-nurse-label">Nurse (Incharge)</InputLabel>
              <Select
                labelId="select-nurse-label"
                value={selectedNurseId}
                label="Nurse (Incharge)"
                onChange={(e) => setSelectedNurseId(e.target.value)}
              >
                {nurses.map((n) => (
                  <MenuItem key={n.nurse_id} value={n.nurse_id}>
                    {n.name} {n.specialization ? `(${n.specialization})` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAssign}
            disabled={!selectedWard || !selectedNurseId || submitting}
            sx={{
              bgcolor: NURSE_COLORS.primary,
              "&:hover": { bgcolor: "#092542" },
              textTransform: "none",
            }}
          >
            {submitting ? "Assigning..." : "Confirm Assignment"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
